import { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Paper, Typography, Alert, AlertTitle, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loading from "../../layout/Loading";
import { getHouseTodayConsumption, getHouseLastHourConsumption } from "../../../api_calls/api_calls_houses";
import { useTheme } from '@mui/material/styles';

function HouseMonitoringConsumptionFeed({ id }) {
    const theme = useTheme();
    const [labels, setLabels] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false)
    const [dataError, setDataError] = useState(false)

    // State for last hour chart
    const [hourLabels, setHourLabels] = useState([]);
    const [hourData, setHourData] = useState([]);
    const [hourLoading, setHourLoading] = useState(false);
    const [hourError, setHourError] = useState(false);

    const [chartIndex, setChartIndex] = useState(0); // 0: Today, 1: Last Hour

    const handleToggleChart = (direction) => {
        setChartIndex((prev) => {
            if (direction === 'left') return prev === 0 ? 1 : 0;
            if (direction === 'right') return prev === 1 ? 0 : 1;
            return prev;
        });
    };
    const handleDotClick = (index) => {
        setChartIndex(index);
    };

    useEffect(() => {
        setLoading(true);
        setDataError(false);
        getHouseTodayConsumption(id)
            .then(response => {
                setLoading(false);
                setLabels(response.labels);
                setData(response.consumption);
            })
            .catch(error => {
                setLoading(false);
                setDataError(true);
            });

        setHourLoading(true);
        setHourError(false);
        getHouseLastHourConsumption(id)
            .then(response => {
                setHourLoading(false);
                setHourLabels(response.labels);
                setHourData(response.consumption);
            })
            .catch(error => {
                setHourLoading(false);
                setHourError(true);
            });
    }, [id]);

    const option = {
        tooltip: {
            trigger: 'axis',
            backgroundColor: theme.palette.common.white,
            borderColor: theme.palette.grey[300],
            borderWidth: 1,
            padding: 10,
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: labels,
        },
        yAxis: {
            type: 'value',
            name: 'Consumption (wh)',
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
                margin: 10,
                fontSize: 12,
            },
        },
        series: [
            {
                data: data,
                type: 'line',
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            {
                                offset: 0,
                                color: theme.palette.secondary.main, // Top (high values) - secondary
                            },
                            {
                                offset: 1,
                                color: theme.palette.common.white, // Bottom (low values) - white
                            },
                        ],
                    },
                },
                lineStyle: {
                    width: 3,
                    color: theme.palette.secondary.main, // Secondary color
                },
                showSymbol: false,
            },
        ],
        grid: {
            left: '0%',
            right: '0%',
            top: '5%',
            bottom: '5%',
            containLabel: true,
        },
    };

    const hourOption = {
        tooltip: {
            trigger: 'axis',
            backgroundColor: theme.palette.common.white,
            borderColor: theme.palette.grey[300],
            borderWidth: 1,
            padding: 10,
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: hourLabels,
        },
        yAxis: {
            type: 'value',
            name: 'Consumption (wh)',
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
                margin: 10,
                fontSize: 12,
            },
        },
        series: [
            {
                data: hourData,
                type: 'line',
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            {
                                offset: 0,
                                color: theme.palette.secondary.main, // Top (high values) - secondary
                            },
                            {
                                offset: 1,
                                color: theme.palette.common.white, // Bottom (low values) - white
                            },
                        ],
                    },
                },
                lineStyle: {
                    width: 3,
                    color: theme.palette.secondary.main, // Secondary color
                },
                showSymbol: false,
            },
        ],
        grid: {
            left: '0%',
            right: '0%',
            top: '5%',
            bottom: '5%',
            containLabel: true,
        },
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Paper
                elevation={0}
                style={{
                    padding: '20px',
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                {chartIndex === 0 ? (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }} className={'fancyText'}>Today's Consumption</Typography>
                        <div style={{ flex: 1 }}>
                            {loading && <div style={{ margin: 'auto' }}><Loading /></div>}
                            {!loading && dataError &&
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    <AlertTitle>Error</AlertTitle>
                                    Something went wrong while fetching the data. Please try again later.
                                </Alert>
                            }
                            {!loading && !dataError && <ReactECharts option={option} style={{ width: '100%' }} />}
                        </div>
                    </>
                ) : (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }} className={'fancyText'}>Last Hour Consumption</Typography>
                        <div style={{ flex: 1 }}>
                            {hourLoading && <div style={{ margin: 'auto' }}><Loading /></div>}
                            {!hourLoading && hourError &&
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    <AlertTitle>Error</AlertTitle>
                                    Something went wrong while fetching the data. Please try again later.
                                </Alert>
                            }
                            {!hourLoading && !hourError && <ReactECharts option={hourOption} style={{ width: '100%' }} />}
                        </div>
                    </>
                )}
                {/* Pagination Dots & Chevrons */}
                <Box sx={{ width: '100%', position: 'absolute', bottom: 16, left: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                    {[0, 1].map((idx) => (
                        <Box
                            key={idx}
                            onClick={() => handleDotClick(idx)}
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: chartIndex === idx ? theme.palette.secondary.main : theme.palette.grey[300],
                                mx: 0.5,
                                transition: 'background-color 0.3s',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                            }}
                        />
                    ))}
                </Box>
                <Box sx={{ position: 'absolute', bottom: 12, right: 16, display: 'flex', alignItems: 'center', zIndex: 2 }}>
                    <IconButton
                        onClick={() => handleToggleChart('left')}
                        aria-label="previous chart"
                        size="small"
                        disabled={chartIndex === 0}
                        sx={{ mx: 0.5 }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => handleToggleChart('right')}
                        aria-label="next chart"
                        size="small"
                        disabled={chartIndex === 1}
                        sx={{ mx: 0.5 }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
                {/* Add margin-bottom to chart area so handlers are not too close */}
                <Box sx={{ mb: 5 }} />
            </Paper>
        </Box>
    );
}

export default HouseMonitoringConsumptionFeed;
